/**
 * <RoleGuard role="admin"> + <TierGuard tier="cabinet"> - hide forbidden content.
 * Per CHAP-65.
 */
import type { ReactNode } from 'react';
import { useUserRoles } from '../hooks/useUserRoles.js';
import type { Role, CustomerTier } from '../../shared/types.js';

interface RoleGuardProps {
  role: Role | Role[];
  fallback?: ReactNode;
  children: ReactNode;
}

export function RoleGuard({ role, fallback = null, children }: RoleGuardProps) {
  const { allowedRoles, isLoading } = useUserRoles();
  if (isLoading) return null;
  const required = Array.isArray(role) ? role : [role];
  const ok = required.some((r) => allowedRoles.includes(r));
  return <>{ok ? children : fallback}</>;
}

interface TierGuardProps {
  tier: CustomerTier | CustomerTier[];
  fallback?: ReactNode;
  children: ReactNode;
}

export function TierGuard({ tier, fallback = null, children }: TierGuardProps) {
  const { tier: userTier, allowedRoles, isLoading } = useUserRoles();
  if (isLoading) return null;
  const required = Array.isArray(tier) ? tier : [tier];
  const bypass = allowedRoles.includes('team') || allowedRoles.includes('admin');
  const tierMatch = userTier && required.includes(userTier);
  return <>{bypass || tierMatch ? children : fallback}</>;
}

/**
 * Restricted-tier gate (CHAP-82/86) — voter/board/cabinet (+ team/admin) only.
 * Matches the `restricted` sensitivity tier in data-classification.md: observers
 * do not see this content. `label` names the gated surface in the fallback.
 */
export function RestrictedTierGate({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <TierGuard
      tier={['voter', 'board', 'cabinet']}
      fallback={
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-sm text-neutral min-w-0">
          {label ?? 'This content'} is available to voting members and above. Ask a Cabinet member if you need access.
        </div>
      }
    >
      {children}
    </TierGuard>
  );
}

/**
 * Confidential-tier gate (CHAP-83) — board/cabinet (+ team/admin) only.
 *
 * Matches the `confidential` sensitivity tier in data-classification.md:
 * "Customer-role with tier >= board" — donor capacity ratings, individual
 * financial discussions, alumni databases. Strictly narrower than
 * RestrictedTierGate: a `voter` sees restricted content but NOT confidential.
 */
export function ConfidentialTierGate({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <TierGuard
      tier={['board', 'cabinet']}
      fallback={
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-sm text-neutral min-w-0">
          {label ?? 'This content'} contains donor-capacity detail and is available to board members and above. Ask a Cabinet member if you need access.
        </div>
      }
    >
      {children}
    </TierGuard>
  );
}
